import * as React from 'react';
import {useEffect, useState} from 'react';
import {Exceptions, EXCLUDE_ST_LIMIT} from '../../exceptions/exceptions.class';
import {User} from '../../users/user.class';
import {ShipStateExceptionsModal} from '../../orders/ShipStateExceptionsModal';
import {faAngleRight} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {GdspSdsAsset} from '../item.class';
import Button from '../../ui/Buttons/Button';

type IDPExceptionsProps = {
    exceptions: Exceptions;
    gdspSdsAsset?: GdspSdsAsset;
    user: User;
};

export const IDPExceptions = ({exceptions, gdspSdsAsset, user}: IDPExceptionsProps) => {
    const [excludeStSeeMore, setExcludeStSeeMore] = useState(false);
    const [excludeStSeeMoreModal, setExcludeStSeeMoreModal] = useState(false);
    const [shortList, setShortList] = useState(exceptions.excludeStArray?.join(` `));

    useEffect(() => {
        if (exceptions.excludeStArray?.length > 0) {
            if (shortList.length > EXCLUDE_ST_LIMIT) {
                setExcludeStSeeMore(true);
                setShortList(shortList.substring(0, EXCLUDE_ST_LIMIT));
            }
        }
    }, [exceptions.excludeStArray?.length, shortList]);

    const getSDSLink = () => {
        if (exceptions.sdsAssetFileName_s) {
            return (
                <>
                    {' '}
                    <a
                        href={`/SDS/${exceptions.id}`}
                        target="_blank"
                    >
                        View
                    </a>{' '}
                    |{' '}
                    <a
                        href={`/SDS/download/${exceptions.id}/${encodeURIComponent(exceptions.sdsAssetFileName_s)}`}
                        target="_blank"
                    >
                        Download
                    </a>
                </>
            );
        } else if (gdspSdsAsset) {
            return (
                <a
                    href={`https://www.grainger.com/sds/pdf/${gdspSdsAsset.assetID}.pdf`}
                    rel="nofollow"
                    target="_blank"
                >
                    View
                </a>
            );
        } else if (exceptions.sds_s) {
            return (
                <a
                    href={`/SDS/${exceptions.sds_s}`}
                    target="_blank"
                >
                    SDS Available
                </a>
            );
        }
    };

    return (
        <div>
            {user?.hasCustomCatalogs() && !exceptions.itemNeedsAppr && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="Custom Catalog Icon"
                            className="tw-max-w-[30px]"
                            src="/btns/custom-catalog-icon.gif"
                            title="Approved for your custom catalog"
                        />
                    </div>
                    <div className="tw-w-10/12">Approved for your custom catalog.</div>
                </div>
            )}
            {(exceptions.prop65b_b === true || exceptions.prop65c_b === true || exceptions.prop65Message) && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="California Proposition 65 Icon"
                            className="tw-max-w-[30px] tw-h-[26px]"
                            src="/btns/warning.png"
                            title={exceptions.prop65Message}
                        />
                    </div>
                    <div className="tw-w-10/12">WARNING: {exceptions.prop65Message}</div>
                </div>
            )}
            {exceptions.canShipAir === false && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="Product cannot be shipped AIR freight"
                            className="tw-max-w-[30px]"
                            src="/btns/AirshipExclusion.png"
                            title="Product cannot be shipped AIR freight"
                        />
                    </div>
                    <div className="tw-w-10/12">
                        Air Shipment Prohibited{' '}
                        <a href="/help/hazfrt">
                            Details{' '}
                            <FontAwesomeIcon
                                icon={faAngleRight}
                                suppressHydrationWarning
                            />
                        </a>
                    </div>
                </div>
            )}
            {exceptions.excludeStArray && exceptions.excludeStArray.length > 0 && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="State ship exclusion"
                            className="tw-max-w-[30px]"
                            src="/btns/StateShipExclusion.png"
                            title="State ship exclusion"
                        />
                    </div>
                    <div className="tw-w-10/12">
                        Shipping Exclusions: {shortList}
                        {excludeStSeeMore && (
                            <Button
                                variant="link"
                                className="tw-ml-2"
                                onClick={() => {
                                    setExcludeStSeeMoreModal(true);
                                }}
                                size="sm"
                            >
                                View All
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {exceptions.upsHazard && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="Hazardous Freight Charge"
                            className="tw-max-w-[30px]"
                            src="/btns/HazardousFreightCharge.png"
                            title="Hazardous Freight Charge"
                        />
                    </div>
                    <div className="tw-w-10/12">Hazardous Material</div>
                </div>
            )}
            {exceptions.ormd && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="ORM-D Product"
                            className="tw-max-w-[30px]"
                            src="/btns/ORM_D.png"
                            title="ORM-D Product"
                        />
                    </div>
                    <div className="tw-w-10/12">
                        Limited Quantity{' '}
                        <a href="/help/hazfrt">
                            Details{' '}
                            <FontAwesomeIcon
                                icon={faAngleRight}
                                suppressHydrationWarning
                            />
                        </a>
                    </div>
                </div>
            )}
            {(exceptions.lotTrack || exceptions.lottracking) && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="Lot Tracked Item"
                            className="tw-max-w-[30px]"
                            src="/btns/LotTrackedItem.png"
                            title="Lot Tracked Item"
                        />
                    </div>
                    <div className="tw-w-10/12">
                        Lot Tracked Item{' '}
                        <a href="/services/lot-tracking">
                            Details{' '}
                            <FontAwesomeIcon
                                icon={faAngleRight}
                                suppressHydrationWarning
                            />
                        </a>
                    </div>
                </div>
            )}
            {(exceptions.sdsLink || exceptions.msdsavail || exceptions.graMsdsavail_b || exceptions.sdsAssetFileName_s) && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="SDS Available"
                            className="tw-max-w-[30px]"
                            src="/btns/SDSSheet.png"
                            title="SDS Available"
                        />
                    </div>
                    <div className="tw-w-10/12">SDS Available {getSDSLink()}</div>
                </div>
            )}
            {exceptions.bin && (
                <div className="tw-flex tw-mb-3">
                    <div className="tw-w-2/12 md:tw-w-1/12 tw-flex tw-items-center tw-justify-center">
                        <img
                            alt="Labeled in shop"
                            className="tw-max-w-[30px]"
                            src="/btns/ItemLabeledInShop.png"
                            title="Labeled in shop"
                        />
                    </div>
                    <div className="tw-w-10/12">Labeled in shop</div>
                </div>
            )}
            <ShipStateExceptionsModal
                onHide={() => {
                    setExcludeStSeeMoreModal(false);
                }}
                shipStateExclusions={exceptions.excludeStArray}
                show={excludeStSeeMoreModal}
            />
        </div>
    );
};
